import { useEffect, useState } from "react";
import { Download, Eye, FileCheck2, FileText, FolderLock, Image as ImageIcon, Video } from "lucide-react";
import { api, domaineLabel, fmtDate } from "@/lib/api";
import DocumentViewer, { downloadDocument } from "@/components/DocumentViewer";
import { useAuth } from "@/context/AuthContext";

const iconFor = (contentType = "") => {
  if (contentType.startsWith("image/")) return ImageIcon;
  if (contentType.startsWith("video/")) return Video;
  return FileText;
};

export const openDocument = (url, filename) => downloadDocument(url, filename);

export default function Documents() {
  const { user } = useAuth();
  const contrat = user?.contact?.contrat;
  const [categorie, setCategorie] = useState("technique");
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [apercu, setApercu] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get("/documents", { params: { categorie } }).then(({ data }) => {
      setDocs(data);
      setLoading(false);
    });
  }, [categorie]);

  return (
    <div className="space-y-7" data-testid="documents-page">
      <div>
        <div className="bk-label mb-2">Mes documents</div>
        <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter text-slate-900">Coffre-fort</h1>
        <p className="text-sm text-slate-600 mt-2">Votre contrat, les notices de votre matériel et les documents administratifs de vos chantiers, consultables directement dans l'application.</p>
      </div>

      {contrat && (
        <div
          className={`rounded-xl border px-4 py-3 flex items-start gap-3 ${contrat.actif ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`}
          data-testid="contrat-banner"
        >
          <FileCheck2 size={18} className={contrat.actif ? "text-emerald-600 mt-0.5" : "text-amber-600 mt-0.5"} />
          <div className="text-sm">
            <div className="bk-label mb-1">{contrat.actif ? "Contrat actif" : "Hors contrat"}</div>
            <div className="font-semibold text-slate-900">{contrat.libelle}</div>
            <div className="text-slate-600">
              {contrat.actif ? `Couverture : ${contrat.couverture} · échéance ${contrat.echeance}` : "Intervention facturée hors contrat"}
            </div>
          </div>
        </div>
      )}

      <div className="flex gap-2">
        {[
          { key: "technique", label: "Techniques / matériel" },
          { key: "administratif", label: "Administratifs" },
        ].map((c) => (
          <button
            key={c.key}
            data-testid={`docs-filter-${c.key}`}
            onClick={() => setCategorie(c.key)}
            className={`text-sm px-4 py-2 rounded-full border transition-colors ${categorie === c.key ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 text-slate-600 hover:border-slate-400"}`}
          >
            {c.label}
          </button>
        ))}
      </div>

      {loading && <div className="text-sm text-slate-400">Chargement…</div>}

      {!loading && docs.length === 0 && (
        <div className="rounded-2xl border border-dashed border-slate-300 p-10 text-center" data-testid="docs-empty">
          <FolderLock size={26} className="mx-auto text-slate-300 mb-3" />
          <p className="text-sm text-slate-500">Aucun document disponible dans cette catégorie.</p>
        </div>
      )}

      <div className="space-y-3">
        {docs.map((d) => {
          const Icon = iconFor(d.content_type);
          return (
            <div
              key={d.id}
              data-testid={`doc-card-${d.id}`}
              className="flex items-center gap-4 rounded-xl border border-slate-200 bg-white px-4 py-4 hover:border-[#005CFF] transition-colors"
            >
              <Icon size={18} className="text-[#005CFF] shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-slate-900 truncate">{d.titre}</div>
                <div className="text-xs text-slate-500 truncate">
                  {d.site_nom || "Tous sites"} {d.materiel ? `· ${d.materiel}` : ""} {d.domaine ? `· ${domaineLabel(d.domaine)}` : ""} · {Math.round((d.size || 0) / 1024)} Ko · {fmtDate(d.created_at)}
                </div>
                {d.description && <div className="text-xs text-slate-500 mt-1 line-clamp-2">{d.description}</div>}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {d.previewable && (
                  <button
                    data-testid={`doc-preview-${d.id}`}
                    onClick={() => setApercu(d)}
                    title="Aperçu"
                    className="flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full bg-slate-900 text-white hover:bg-[#005CFF] transition-colors"
                  >
                    <Eye size={14} /> <span className="hidden sm:inline">Aperçu</span>
                  </button>
                )}
                <button
                  data-testid={`doc-download-${d.id}`}
                  onClick={() => downloadDocument(`/documents/${d.id}/download`, d.filename)}
                  title="Télécharger"
                  className="p-2 rounded-full text-slate-400 hover:text-[#005CFF] hover:bg-slate-100 transition-colors"
                >
                  <Download size={16} />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {apercu && <DocumentViewer doc={apercu} url={`/documents/${apercu.id}/download`} onClose={() => setApercu(null)} />}
    </div>
  );
}
